import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread'
import { useTranslation } from 'next-i18next'
import { IEmailDeliverability } from 'src/types/graphql'
import { formatTimestampMonthDayYear } from 'src/utils'

/**
 * Deliverability badge for `User.emailDeliverability`.
 *
 * The ONLY place the four statuses are turned into UI, so the two admin
 * surfaces (user search row + `/user/[id]` profile) can never disagree.
 *
 * Two rendered states, and the distinction is the whole point of the feature:
 *   · `undeliverable` → RED. We will refuse to send to this address.
 *   · `unknown`       → GREY. The suppression store could NOT BE READ. This is
 *                       NOT a clean bill of health, so it must not look like
 *                       one to the only audience equipped to act on it.
 *   · `ok` / `hidden` → nothing.
 *
 * Never gate on `isUndeliverable` — that boolean collapses `unknown` into the
 * healthy branch, which is the exact failure mode this badge exists to expose.
 * Always compare `status` explicitly.
 *
 * No permission check here: the SERVER already gated the field and returns
 * `status: 'hidden'` to viewers who may not see it. Re-gating on the client's
 * narrow `isAdmin` flag would reintroduce the role-only-admin blindness the
 * server-side resolver deliberately avoids.
 */
export default function EmailDeliverabilityPill({
  deliverability,
  variant = 'pill'
}: {
  deliverability?: IEmailDeliverability | null
  /** `pill` = dense admin list row · `inline` = editorial profile badge row. */
  variant?: 'pill' | 'inline'
}) {
  const { t: tAdmin } = useTranslation('admin')
  const status = deliverability?.status

  if (status !== 'undeliverable' && status !== 'unknown') return null

  const isDead = status === 'undeliverable'
  const label = isDead
    ? tAdmin('USER_SEARCH.EMAIL_DEAD_BADGE')
    : tAdmin('USER_SEARCH.EMAIL_UNKNOWN_BADGE')
  const tooltip = isDead
    ? tAdmin('USER_SEARCH.EMAIL_DEAD_TOOLTIP', {
        reason: deliverability?.reason ?? '—',
        count: deliverability?.failureCount ?? 0,
        // `lastSeenAt` is `Date.toUTCString()`, not ISO-8601 — this helper does
        // `new Date(date)` and guards `isNaN`, so the format is safe here.
        date: formatTimestampMonthDayYear(deliverability?.lastSeenAt) || '—'
      })
    : tAdmin('USER_SEARCH.EMAIL_UNKNOWN_TOOLTIP')

  if (variant === 'inline') {
    return (
      <span
        title={tooltip}
        className={`inline-flex items-center gap-2 text-[12px] tracking-[0.08em] uppercase ${
          isDead ? 'text-ink-700' : 'text-ink-200'
        }`}
      >
        <MarkEmailUnreadIcon
          sx={{ fontSize: 14 }}
          aria-hidden="true"
          className={isDead ? 'text-red-500' : undefined}
        />
        {label}
      </span>
    )
  }

  return (
    <span
      className={`group relative inline-flex shrink-0 items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
        isDead ? 'bg-red-100' : 'bg-grey-300'
      } text-ink-700`}
    >
      <MarkEmailUnreadIcon
        sx={{ fontSize: 10 }}
        aria-hidden="true"
        className={isDead ? 'text-red-500' : 'text-grey-600'}
      />
      {label}
      <span className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 hidden -translate-x-1/2 rounded-md bg-ink-800 px-2 py-1 text-[10px] font-medium whitespace-nowrap text-white-500 group-hover:block">
        {tooltip}
      </span>
    </span>
  )
}
